import { useMutation, useQuery } from '@tanstack/react-query';
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    MutationFunction,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';

import { useCallback } from 'react';

import type {
    BulkResponse,
    GetAllIdentityRolesParams,
    GetAllIdentityRolesResponse,
    GetApiResourceRoleAssignmentsParams,
    GetIdentityPermissionAssignmentsParams,
    GetIdentityRoleAssignmentsParams,
    GetPermissionAssignmentsResponse,
    GetRoleAssignmentsResponse,
    GetRoleAssignmentsWithContextResponse,
    GetRoleImportTemplateResponse,
    IdentityRoleAssignmentRequestModel,
    ProblemDetails,
    RoleAssignmentRequestModel,
    RoleImportModel,
} from '../models';

import { useCustomIDSClient } from '../../custom-client';
import type { ErrorType } from '../../custom-client';

/**
 * <br><b>Required Scopes</b><br>login.umdaschgroup.com/permissions.write
 */
export const useAssignRevokeApiResourceRolesHook = () => {
    const assignRevokeApiResourceRoles = useCustomIDSClient<BulkResponse>();

    return useCallback(
        (roleId: string, roleAssignmentRequestModel: RoleAssignmentRequestModel, signal?: AbortSignal) => {
            return assignRevokeApiResourceRoles({
                url: `/roles/api-resource-roles/${roleId}/bulkassignrevoke`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: roleAssignmentRequestModel,
                signal,
            });
        },
        [assignRevokeApiResourceRoles],
    );
};

export const useAssignRevokeApiResourceRolesMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useAssignRevokeApiResourceRolesHook>>>,
        TError,
        { roleId: string; data: RoleAssignmentRequestModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useAssignRevokeApiResourceRolesHook>>>,
    TError,
    { roleId: string; data: RoleAssignmentRequestModel },
    TContext
> => {
    const mutationKey = ['assignRevokeApiResourceRoles'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const assignRevokeApiResourceRoles = useAssignRevokeApiResourceRolesHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useAssignRevokeApiResourceRolesHook>>>,
        { roleId: string; data: RoleAssignmentRequestModel }
    > = (props) => {
        const { roleId, data } = props ?? {};

        return assignRevokeApiResourceRoles(roleId, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type AssignRevokeApiResourceRolesMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useAssignRevokeApiResourceRolesHook>>>
>;
export type AssignRevokeApiResourceRolesMutationBody = RoleAssignmentRequestModel;
export type AssignRevokeApiResourceRolesMutationError = ErrorType<ProblemDetails>;

export const useAssignRevokeApiResourceRoles = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useAssignRevokeApiResourceRolesHook>>>,
            TError,
            { roleId: string; data: RoleAssignmentRequestModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useAssignRevokeApiResourceRolesHook>>>,
    TError,
    { roleId: string; data: RoleAssignmentRequestModel },
    TContext
> => {
    return useMutation(useAssignRevokeApiResourceRolesMutationOptions(options), queryClient);
};
/**
 * <br><b>Required Scopes</b><br>login.umdaschgroup.com/permissions.read
 */
export const useGetApiResourceRoleAssignmentsHook = () => {
    const getApiResourceRoleAssignments = useCustomIDSClient<GetRoleAssignmentsResponse>();

    return useCallback(
        (params?: GetApiResourceRoleAssignmentsParams, signal?: AbortSignal) => {
            return getApiResourceRoleAssignments({
                url: `/roles/api-resource-roles/role-assignments`,
                method: 'GET',
                params,
                signal,
            });
        },
        [getApiResourceRoleAssignments],
    );
};

export const getGetApiResourceRoleAssignmentsQueryKey = (params?: GetApiResourceRoleAssignmentsParams) => {
    return [`/roles/api-resource-roles/role-assignments`, ...(params ? [params] : [])] as const;
};

export const useGetApiResourceRoleAssignmentsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleAssignmentsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetApiResourceRoleAssignmentsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleAssignmentsHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetApiResourceRoleAssignmentsQueryKey(params);

    const getApiResourceRoleAssignments = useGetApiResourceRoleAssignmentsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleAssignmentsHook>>>> = ({
        signal,
    }) => getApiResourceRoleAssignments(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleAssignmentsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetApiResourceRoleAssignmentsQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleAssignmentsHook>>>
>;
export type GetApiResourceRoleAssignmentsQueryError = ErrorType<ProblemDetails>;

export function useGetApiResourceRoleAssignments<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleAssignmentsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: undefined | GetApiResourceRoleAssignmentsParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleAssignmentsHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleAssignmentsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleAssignmentsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResourceRoleAssignments<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleAssignmentsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetApiResourceRoleAssignmentsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleAssignmentsHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleAssignmentsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleAssignmentsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResourceRoleAssignments<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleAssignmentsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetApiResourceRoleAssignmentsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleAssignmentsHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetApiResourceRoleAssignments<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleAssignmentsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetApiResourceRoleAssignmentsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleAssignmentsHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetApiResourceRoleAssignmentsQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * <br><b>Required Scopes</b><br>login.umdaschgroup.com/permissions.read
 */
export const useGetAllIdentityRolesHook = () => {
    const getAllIdentityRoles = useCustomIDSClient<GetAllIdentityRolesResponse>();

    return useCallback(
        (params?: GetAllIdentityRolesParams, signal?: AbortSignal) => {
            return getAllIdentityRoles({ url: `/roles/identity-roles`, method: 'GET', params, signal });
        },
        [getAllIdentityRoles],
    );
};

export const getGetAllIdentityRolesQueryKey = (params?: GetAllIdentityRolesParams) => {
    return [`/roles/identity-roles`, ...(params ? [params] : [])] as const;
};

export const useGetAllIdentityRolesQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetAllIdentityRolesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetAllIdentityRolesParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAllIdentityRolesHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetAllIdentityRolesQueryKey(params);

    const getAllIdentityRoles = useGetAllIdentityRolesHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetAllIdentityRolesHook>>>> = ({ signal }) =>
        getAllIdentityRoles(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetAllIdentityRolesHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAllIdentityRolesQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetAllIdentityRolesHook>>>
>;
export type GetAllIdentityRolesQueryError = ErrorType<ProblemDetails>;

export function useGetAllIdentityRoles<
    TData = Awaited<ReturnType<ReturnType<typeof useGetAllIdentityRolesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: undefined | GetAllIdentityRolesParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAllIdentityRolesHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetAllIdentityRolesHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetAllIdentityRolesHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllIdentityRoles<
    TData = Awaited<ReturnType<ReturnType<typeof useGetAllIdentityRolesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetAllIdentityRolesParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAllIdentityRolesHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetAllIdentityRolesHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetAllIdentityRolesHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllIdentityRoles<
    TData = Awaited<ReturnType<ReturnType<typeof useGetAllIdentityRolesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetAllIdentityRolesParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAllIdentityRolesHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetAllIdentityRoles<
    TData = Awaited<ReturnType<ReturnType<typeof useGetAllIdentityRolesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetAllIdentityRolesParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAllIdentityRolesHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetAllIdentityRolesQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * <br><b>Required Scopes</b><br>login.umdaschgroup.com/permissions.write
 */
export const useAssignRevokeIdentityRolesHook = () => {
    const assignRevokeIdentityRoles = useCustomIDSClient<BulkResponse>();

    return useCallback(
        (
            roleId: string,
            identityRoleAssignmentRequestModel: IdentityRoleAssignmentRequestModel,
            signal?: AbortSignal,
        ) => {
            return assignRevokeIdentityRoles({
                url: `/roles/identity-roles/${roleId}/bulkassignrevoke`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: identityRoleAssignmentRequestModel,
                signal,
            });
        },
        [assignRevokeIdentityRoles],
    );
};

export const useAssignRevokeIdentityRolesMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useAssignRevokeIdentityRolesHook>>>,
        TError,
        { roleId: string; data: IdentityRoleAssignmentRequestModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useAssignRevokeIdentityRolesHook>>>,
    TError,
    { roleId: string; data: IdentityRoleAssignmentRequestModel },
    TContext
> => {
    const mutationKey = ['assignRevokeIdentityRoles'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const assignRevokeIdentityRoles = useAssignRevokeIdentityRolesHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useAssignRevokeIdentityRolesHook>>>,
        { roleId: string; data: IdentityRoleAssignmentRequestModel }
    > = (props) => {
        const { roleId, data } = props ?? {};

        return assignRevokeIdentityRoles(roleId, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type AssignRevokeIdentityRolesMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useAssignRevokeIdentityRolesHook>>>
>;
export type AssignRevokeIdentityRolesMutationBody = IdentityRoleAssignmentRequestModel;
export type AssignRevokeIdentityRolesMutationError = ErrorType<ProblemDetails>;

export const useAssignRevokeIdentityRoles = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useAssignRevokeIdentityRolesHook>>>,
            TError,
            { roleId: string; data: IdentityRoleAssignmentRequestModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useAssignRevokeIdentityRolesHook>>>,
    TError,
    { roleId: string; data: IdentityRoleAssignmentRequestModel },
    TContext
> => {
    return useMutation(useAssignRevokeIdentityRolesMutationOptions(options), queryClient);
};
/**
 * <br><b>Required Scopes</b><br>login.umdaschgroup.com/permissions.read
 */
export const useGetRoleImportTemplateHook = () => {
    const getRoleImportTemplate = useCustomIDSClient<GetRoleImportTemplateResponse>();

    return useCallback(
        (signal?: AbortSignal) => {
            return getRoleImportTemplate({ url: `/roles/identity-roles/export`, method: 'GET', signal });
        },
        [getRoleImportTemplate],
    );
};

export const getGetRoleImportTemplateQueryKey = () => {
    return [`/roles/identity-roles/export`] as const;
};

export const useGetRoleImportTemplateQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetRoleImportTemplateHook>>>,
    TError = ErrorType<ProblemDetails>,
>(options?: {
    query?: Partial<
        UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRoleImportTemplateHook>>>, TError, TData>
    >;
}) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetRoleImportTemplateQueryKey();

    const getRoleImportTemplate = useGetRoleImportTemplateHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetRoleImportTemplateHook>>>> = ({ signal }) =>
        getRoleImportTemplate(signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetRoleImportTemplateHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetRoleImportTemplateQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetRoleImportTemplateHook>>>
>;
export type GetRoleImportTemplateQueryError = ErrorType<ProblemDetails>;

export function useGetRoleImportTemplate<
    TData = Awaited<ReturnType<ReturnType<typeof useGetRoleImportTemplateHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRoleImportTemplateHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetRoleImportTemplateHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetRoleImportTemplateHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetRoleImportTemplate<
    TData = Awaited<ReturnType<ReturnType<typeof useGetRoleImportTemplateHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRoleImportTemplateHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetRoleImportTemplateHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetRoleImportTemplateHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetRoleImportTemplate<
    TData = Awaited<ReturnType<ReturnType<typeof useGetRoleImportTemplateHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRoleImportTemplateHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetRoleImportTemplate<
    TData = Awaited<ReturnType<ReturnType<typeof useGetRoleImportTemplateHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRoleImportTemplateHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetRoleImportTemplateQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * <br><b>Required Scopes</b><br>login.umdaschgroup.com/permissions.write
 */
export const useImportIdentityRolesHook = () => {
    const importIdentityRoles = useCustomIDSClient<void>();

    return useCallback(
        (roleImportModel: RoleImportModel, signal?: AbortSignal) => {
            return importIdentityRoles({
                url: `/roles/identity-roles/import`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: roleImportModel,
                signal,
            });
        },
        [importIdentityRoles],
    );
};

export const useImportIdentityRolesMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useImportIdentityRolesHook>>>,
        TError,
        { data: RoleImportModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useImportIdentityRolesHook>>>,
    TError,
    { data: RoleImportModel },
    TContext
> => {
    const mutationKey = ['importIdentityRoles'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const importIdentityRoles = useImportIdentityRolesHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useImportIdentityRolesHook>>>,
        { data: RoleImportModel }
    > = (props) => {
        const { data } = props ?? {};

        return importIdentityRoles(data);
    };

    return { mutationFn, ...mutationOptions };
};

export type ImportIdentityRolesMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useImportIdentityRolesHook>>>
>;
export type ImportIdentityRolesMutationBody = RoleImportModel;
export type ImportIdentityRolesMutationError = ErrorType<ProblemDetails>;

export const useImportIdentityRoles = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useImportIdentityRolesHook>>>,
            TError,
            { data: RoleImportModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useImportIdentityRolesHook>>>,
    TError,
    { data: RoleImportModel },
    TContext
> => {
    return useMutation(useImportIdentityRolesMutationOptions(options), queryClient);
};
/**
 * <br><b>Required Scopes</b><br>login.umdaschgroup.com/permissions.read
 */
export const useGetIdentityPermissionAssignmentsHook = () => {
    const getIdentityPermissionAssignments = useCustomIDSClient<GetPermissionAssignmentsResponse>();

    return useCallback(
        (params?: GetIdentityPermissionAssignmentsParams, signal?: AbortSignal) => {
            return getIdentityPermissionAssignments({
                url: `/roles/identity-roles/permission-assignments`,
                method: 'GET',
                params,
                signal,
            });
        },
        [getIdentityPermissionAssignments],
    );
};

export const getGetIdentityPermissionAssignmentsQueryKey = (params?: GetIdentityPermissionAssignmentsParams) => {
    return [`/roles/identity-roles/permission-assignments`, ...(params ? [params] : [])] as const;
};

export const useGetIdentityPermissionAssignmentsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetIdentityPermissionAssignmentsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetIdentityPermissionAssignmentsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<ReturnType<typeof useGetIdentityPermissionAssignmentsHook>>>,
                TError,
                TData
            >
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetIdentityPermissionAssignmentsQueryKey(params);

    const getIdentityPermissionAssignments = useGetIdentityPermissionAssignmentsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetIdentityPermissionAssignmentsHook>>>> = ({
        signal,
    }) => getIdentityPermissionAssignments(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetIdentityPermissionAssignmentsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetIdentityPermissionAssignmentsQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetIdentityPermissionAssignmentsHook>>>
>;
export type GetIdentityPermissionAssignmentsQueryError = ErrorType<ProblemDetails>;

export function useGetIdentityPermissionAssignments<
    TData = Awaited<ReturnType<ReturnType<typeof useGetIdentityPermissionAssignmentsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: undefined | GetIdentityPermissionAssignmentsParams,
    options: {
        query: Partial<
            UseQueryOptions<
                Awaited<ReturnType<ReturnType<typeof useGetIdentityPermissionAssignmentsHook>>>,
                TError,
                TData
            >
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetIdentityPermissionAssignmentsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetIdentityPermissionAssignmentsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetIdentityPermissionAssignments<
    TData = Awaited<ReturnType<ReturnType<typeof useGetIdentityPermissionAssignmentsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetIdentityPermissionAssignmentsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<ReturnType<typeof useGetIdentityPermissionAssignmentsHook>>>,
                TError,
                TData
            >
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetIdentityPermissionAssignmentsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetIdentityPermissionAssignmentsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetIdentityPermissionAssignments<
    TData = Awaited<ReturnType<ReturnType<typeof useGetIdentityPermissionAssignmentsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetIdentityPermissionAssignmentsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<ReturnType<typeof useGetIdentityPermissionAssignmentsHook>>>,
                TError,
                TData
            >
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetIdentityPermissionAssignments<
    TData = Awaited<ReturnType<ReturnType<typeof useGetIdentityPermissionAssignmentsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetIdentityPermissionAssignmentsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<ReturnType<typeof useGetIdentityPermissionAssignmentsHook>>>,
                TError,
                TData
            >
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetIdentityPermissionAssignmentsQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * <br><b>Required Scopes</b><br>login.umdaschgroup.com/permissions.read
 */
export const useGetIdentityRoleAssignmentsHook = () => {
    const getIdentityRoleAssignments = useCustomIDSClient<GetRoleAssignmentsWithContextResponse>();

    return useCallback(
        (params?: GetIdentityRoleAssignmentsParams, signal?: AbortSignal) => {
            return getIdentityRoleAssignments({
                url: `/roles/identity-roles/role-assignments`,
                method: 'GET',
                params,
                signal,
            });
        },
        [getIdentityRoleAssignments],
    );
};

export const getGetIdentityRoleAssignmentsQueryKey = (params?: GetIdentityRoleAssignmentsParams) => {
    return [`/roles/identity-roles/role-assignments`, ...(params ? [params] : [])] as const;
};

export const useGetIdentityRoleAssignmentsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetIdentityRoleAssignmentsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetIdentityRoleAssignmentsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetIdentityRoleAssignmentsHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetIdentityRoleAssignmentsQueryKey(params);

    const getIdentityRoleAssignments = useGetIdentityRoleAssignmentsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetIdentityRoleAssignmentsHook>>>> = ({
        signal,
    }) => getIdentityRoleAssignments(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetIdentityRoleAssignmentsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetIdentityRoleAssignmentsQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetIdentityRoleAssignmentsHook>>>
>;
export type GetIdentityRoleAssignmentsQueryError = ErrorType<ProblemDetails>;

export function useGetIdentityRoleAssignments<
    TData = Awaited<ReturnType<ReturnType<typeof useGetIdentityRoleAssignmentsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: undefined | GetIdentityRoleAssignmentsParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetIdentityRoleAssignmentsHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetIdentityRoleAssignmentsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetIdentityRoleAssignmentsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetIdentityRoleAssignments<
    TData = Awaited<ReturnType<ReturnType<typeof useGetIdentityRoleAssignmentsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetIdentityRoleAssignmentsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetIdentityRoleAssignmentsHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetIdentityRoleAssignmentsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetIdentityRoleAssignmentsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetIdentityRoleAssignments<
    TData = Awaited<ReturnType<ReturnType<typeof useGetIdentityRoleAssignmentsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetIdentityRoleAssignmentsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetIdentityRoleAssignmentsHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetIdentityRoleAssignments<
    TData = Awaited<ReturnType<ReturnType<typeof useGetIdentityRoleAssignmentsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetIdentityRoleAssignmentsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetIdentityRoleAssignmentsHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetIdentityRoleAssignmentsQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}
